import React from 'react';
import {
  Box,
  CircularProgress,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Typography,
} from '@mui/joy';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';

/**
 * ModeSelector - Dropdown-based agent mode selector
 *
 * Props:
 * - modes: Array of mode objects { value, label, description }
 * - selectedMode: Currently selected mode value (string)
 * - onSelect: Callback when a mode is selected
 * - loading: Whether modes are being loaded
 * - disabled: Whether selection is disabled (e.g., agent is busy)
 */
const ModeSelector = ({
  modes = [],
  selectedMode = 'agent',
  onSelect,
  loading = false,
  disabled = false,
}) => {
  // Ensure modes is an array
  const modeList = Array.isArray(modes) ? modes : [];

  // Find current mode object
  const currentMode = modeList.find((m) => m.value === selectedMode)
    || modeList.find((m) => m.value === 'agent')
    || modeList[0]
    || null;

  const displayLabel = currentMode?.label || 'Agent';

  // Don't render if no modes and not loading
  if (!loading && modeList.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        px: 0.5,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneOutlinedIcon
            sx={{
              fontSize: 14,
              color: 'neutral.500',
              flexShrink: 0,
            }}
          />
          <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} />
          <Typography level="body-xs" color="neutral">
            Loading modes...
          </Typography>
        </Box>
      ) : (
        <Dropdown>
          <MenuButton
            variant="plain"
            color="neutral"
            size="sm"
            disabled={disabled}
            startDecorator={
              <TuneOutlinedIcon sx={{ fontSize: 14, color: 'neutral.500' }} />
            }
            endDecorator={
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'neutral.500' }} />
            }
            sx={{
              fontSize: '0.75rem',
              fontWeight: 400,
              minHeight: 28,
              px: 1,
              gap: 0.5,
              '--Button-gap': '4px',
              '&:hover': {
                bgcolor: 'neutral.softHoverBg',
              },
            }}
          >
            {displayLabel}
          </MenuButton>
          <Menu
            placement="bottom-start"
            size="sm"
            sx={{
              minWidth: 220,
              '--ListItemDecorator-size': '24px',
            }}
          >
            {modeList.map((mode) => {
              const isSelected = selectedMode === mode.value;
              return (
                <MenuItem
                  key={mode.value}
                  selected={isSelected}
                  onClick={() => onSelect(mode.value)}
                  sx={{
                    fontSize: '0.8rem',
                    py: 1,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 1,
                    }}
                  >
                    <Typography
                      level="body-sm"
                      sx={{ fontWeight: isSelected ? 500 : 400 }}
                    >
                      {mode.label}
                    </Typography>
                    {isSelected && (
                      <CheckIcon sx={{ fontSize: 16, color: 'primary.500' }} />
                    )}
                  </Box>
                  {mode.description && (
                    <Typography
                      level="body-xs"
                      color="neutral"
                      sx={{
                        mt: 0.25,
                        whiteSpace: 'normal',
                        lineHeight: 1.3,
                      }}
                    >
                      {mode.description}
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Menu>
        </Dropdown>
      )}
    </Box>
  );
};

export default ModeSelector;
